import "./style.css";
import { initAnalytics } from "./analytics";
import { demos } from "./demos/registry";
import { demoCategories, type DemoCategory } from "./demos/types";
import { Gallery, type FrameStats } from "./app/Gallery";
import { initCopyPrompt } from "./copyPrompt";
import { initFirstSimulationPlayground } from "./examples/firstSimulation";

initAnalytics();

const canvas = document.getElementById("stage") as HTMLCanvasElement;
const categoryTabs = document.getElementById("demo-category-tabs") as HTMLElement;
const list = document.getElementById("demo-list") as HTMLUListElement;
const titleEl = document.getElementById("demo-title") as HTMLElement;
const blurbEl = document.getElementById("demo-blurb") as HTMLElement;
const hintEl = document.getElementById("hint") as HTMLElement;
const demoActionsEl = document.getElementById("demo-actions") as HTMLElement;
const debugBtn = document.getElementById("btn-debug") as HTMLButtonElement;
const showcaseBtn = document.getElementById("btn-showcase") as HTMLButtonElement;
const showcaseStatus = document.getElementById("showcase-status") as HTMLElement;
const pauseBtn = document.getElementById("btn-pause") as HTMLButtonElement;
const resetBtn = document.getElementById("btn-reset") as HTMLButtonElement;
const stageWrap = document.querySelector(".stage-wrap") as HTMLElement;

const stat = {
	fps: document.getElementById("stat-fps") as HTMLElement,
	bodies: document.getElementById("stat-bodies") as HTMLElement,
	awake: document.getElementById("stat-awake") as HTMLElement,
	steps: document.getElementById("stat-steps") as HTMLElement,
	sim: document.getElementById("stat-sim") as HTMLElement,
};

function showStats(s: FrameStats): void {
	stat.fps.textContent = String(s.fps);
	stat.bodies.textContent = String(s.bodies);
	stat.awake.textContent = String(s.awake);
	stat.steps.textContent = String(s.steps);
	stat.sim.textContent = s.simMs.toFixed(1);
}

const gallery = new Gallery(canvas, showStats);

type DemoFilter = DemoCategory | "All";

const demoButtons = new Map<string, HTMLButtonElement>();
const filterButtons = new Map<DemoFilter, HTMLButtonElement>();
let activeFilter: DemoFilter = "Featured";
let activeDemoId = demos[0]!.id;
let galleryVisible = true;
const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const showcaseDemos = demos.filter((demo) => demo.category === "Featured");
const showcaseDwellMs = 10_000;
let showcasePlaying = !reducedMotion.matches;
let showcaseTimer = 0;

function select(id: string, options: { user?: boolean; transition?: boolean } = {}): void {
	const demo = demos.find((d) => d.id === id);
	if (!demo) return;
	if (options.user) stopShowcase();

	const load = (): void => {
		activeDemoId = id;
		titleEl.textContent = demo.title;
		blurbEl.textContent = demo.blurb;
		gallery.load(demo);
		gallery.setDebugOverlay(debugBtn.getAttribute("aria-pressed") === "true");
		hintEl.innerHTML = gallery.hint;
		renderDemoActions();
		pauseBtn.textContent = "Pause";
		syncActiveDemoButton();
	};

	if (options.transition && !reducedMotion.matches) {
		stageWrap.classList.add("switching");
		window.setTimeout(() => {
			load();
			stageWrap.classList.remove("switching");
			scheduleShowcase();
		}, 160);
		return;
	}

	load();
	scheduleShowcase();
}

function syncActiveDemoButton(): void {
	demoButtons.forEach((button, id) => button.classList.toggle("active", id === activeDemoId));
}

function renderDemoActions(): void {
	demoActionsEl.replaceChildren();
	for (const action of gallery.actions) {
		const btn = document.createElement("button");
		btn.type = "button";
		btn.className = `ctl${action.variant ? ` ctl-${action.variant}` : ""}`;
		btn.textContent = action.label;
		btn.addEventListener("click", () => {
			stopShowcase();
			action.run();
		});
		demoActionsEl.appendChild(btn);
	}
}

function setFilter(filter: DemoFilter, selectFirst: boolean): void {
	activeFilter = filter;
	filterButtons.forEach((button, key) => button.classList.toggle("active", key === filter));
	renderDemoList();
	if (selectFirst) {
		const first = filteredDemos()[0];
		if (first) select(first.id, { user: true });
	} else {
		syncActiveDemoButton();
	}
}

function filteredDemos(): typeof demos {
	return activeFilter === "All" ? demos : demos.filter((demo) => demo.category === activeFilter);
}

function renderDemoList(): void {
	list.replaceChildren();
	demoButtons.clear();
	for (const demo of filteredDemos()) {
		const li = document.createElement("li");
		const btn = document.createElement("button");
		btn.type = "button";
		btn.innerHTML = `${demo.title}<small>${demo.blurb.split(".")[0]}.</small>`;
		btn.addEventListener("click", () => select(demo.id, { user: true }));
		li.appendChild(btn);
		list.appendChild(li);
		demoButtons.set(demo.id, btn);
	}
}

for (const filter of [...demoCategories, "All" as const]) {
	const btn = document.createElement("button");
	btn.type = "button";
	btn.textContent = filter;
	btn.addEventListener("click", () => {
		stopShowcase();
		setFilter(filter, true);
	});
	categoryTabs.appendChild(btn);
	filterButtons.set(filter, btn);
}

pauseBtn.addEventListener("click", () => {
	stopShowcase();
	const paused = gallery.togglePause();
	pauseBtn.textContent = paused ? "Resume" : "Pause";
});

resetBtn.addEventListener("click", () => {
	stopShowcase();
	gallery.reset();
	gallery.setDebugOverlay(debugBtn.getAttribute("aria-pressed") === "true");
	hintEl.innerHTML = gallery.hint;
	renderDemoActions();
	pauseBtn.textContent = "Pause";
});

debugBtn.addEventListener("click", () => {
	stopShowcase();
	const enabled = debugBtn.getAttribute("aria-pressed") !== "true";
	debugBtn.setAttribute("aria-pressed", String(enabled));
	debugBtn.classList.toggle("active", enabled);
	gallery.setDebugOverlay(enabled);
});

showcaseBtn.addEventListener("click", () => {
	setShowcasePlaying(!showcasePlaying);
});

canvas.addEventListener("pointerenter", () => stopShowcase());
canvas.addEventListener("pointerdown", () => stopShowcase());

function setShowcasePlaying(playing: boolean): void {
	showcasePlaying = playing && !reducedMotion.matches;
	showcaseBtn.setAttribute("aria-pressed", String(showcasePlaying));
	showcaseBtn.textContent = showcasePlaying ? "Pause showcase" : "Play showcase";
	showcaseBtn.classList.toggle("active", showcasePlaying);
	showcaseStatus.hidden = !showcasePlaying;
	scheduleShowcase();
}

function stopShowcase(): void {
	if (showcasePlaying) setShowcasePlaying(false);
}

function scheduleShowcase(): void {
	window.clearTimeout(showcaseTimer);
	if (!showcasePlaying || !galleryVisible || showcaseDemos.length < 2) return;
	showcaseTimer = window.setTimeout(advanceShowcase, showcaseDwellMs);
}

function advanceShowcase(): void {
	if (!showcasePlaying || !galleryVisible) return;
	if (activeFilter !== "Featured") setFilter("Featured", false);
	const index = showcaseDemos.findIndex((demo) => demo.id === activeDemoId);
	const next = showcaseDemos[(index + 1 + showcaseDemos.length) % showcaseDemos.length]!;
	select(next.id, { transition: true });
}

reducedMotion.addEventListener("change", () => {
	if (reducedMotion.matches) setShowcasePlaying(false);
});

setFilter("Featured", false);
select(activeDemoId);
setShowcasePlaying(showcasePlaying);

initCopyPrompt();
initFirstSimulationPlayground();

// Only animate the demo while the stage is on screen.
new IntersectionObserver(
	(entries) => {
		for (const e of entries) {
			galleryVisible = e.isIntersecting;
			gallery.setVisible(e.isIntersecting);
		}
		scheduleShowcase();
	},
	{ threshold: 0 },
).observe(canvas);
